/**
 * Catalog Service for ARGUS Olymp Platform
 *
 * Connects the Data Catalog UI to the olymp-backend REST API.
 */
import { API_ENDPOINTS, OLYMP_API_BASE_URL } from '../config/api';

const CATALOG_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.CATALOG.LIST}`.replace(/\/$/, '');
const LISTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.LISTS.LIST}`.replace(/\/$/, '');
const SETS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.SETS.LIST}`.replace(/\/$/, '');
const REQUESTS_BASE = `${OLYMP_API_BASE_URL}${API_ENDPOINTS.DATA_REQUESTS.LIST}`.replace(/\/$/, '');
const FETCH_TIMEOUT_MS = 10000;
const MAX_PAGES = 50;

function buildCatalogUrl(endpoint) {
  return `${OLYMP_API_BASE_URL}${endpoint}`;
}

export function getParameterLookupId(parameter) {
  if (parameter === null || parameter === undefined) {
    return '';
  }

  if (typeof parameter !== 'object') {
    return String(parameter);
  }

  return String(parameter.parameterId || parameter.id || '');
}

function buildCatalogDetailUrl(id, path = '') {
  const detailUrl = buildCatalogUrl(API_ENDPOINTS.CATALOG.DETAIL.replace('{id}', id)).replace(/\/$/, '');
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  if (!normalizedPath) {
    return `${detailUrl}/`;
  }
  return `${detailUrl}/${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildListUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${LISTS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildSetUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${SETS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

function buildRequestUrl(path = '') {
  const normalizedPath = String(path).replace(/^\/+|\/+$/g, '');
  const base = `${REQUESTS_BASE}/`;
  if (!normalizedPath) {
    return base;
  }
  return `${base}${normalizedPath}/`.replace(/([^:]\/)\/+/g, '$1');
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function getAuthHeaders() {
  const token = localStorage.getItem('authToken');
  const authHeaders = token
    ? { Authorization: `Bearer ${token}` }
    : {};

  return {
    'Content-Type': 'application/json',
    ...authHeaders,
  };
}

function buildRequestHeaders({ includeAuth = true } = {}) {
  if (!includeAuth) {
    return {
      'Content-Type': 'application/json',
    };
  }

  return getAuthHeaders();
}

async function apiFetch(url, options = {}) {
  async function doFetch(includeAuth = true) {
    return fetch(url, {
      method: options.method || 'GET',
      headers: buildRequestHeaders({ includeAuth }),
      body: options.body ? JSON.stringify(options.body) : undefined,
      signal: controller.signal,
    });
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  const response = await doFetch(true)
    .then(async initialResponse => {
      if (
        initialResponse.status === 401
        && options.allowAnonymousRetry
        && localStorage.getItem('authToken')
      ) {
        return doFetch(false);
      }

      return initialResponse;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
    });

  if (!response.ok) {
    const error = new Error(
      response.status === 401
        ? 'Authentication required. Please sign in again.'
        : `API error ${response.status}: ${url}`
    );
    error.status = response.status;
    error.url = url;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

function parseOptionalNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeObject(value) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  return value;
}

function normalizeSynonym(synonym) {
  if (typeof synonym === 'string') {
    return {
      term: synonym,
      language: 'und',
    };
  }

  if (!synonym || typeof synonym !== 'object') {
    return null;
  }

  return {
    ...synonym,
    term: synonym.term || synonym.name || '',
    language: synonym.language || 'und',
  };
}

function normalizeComment(comment) {
  if (!comment || typeof comment !== 'object') {
    return null;
  }

  return {
    ...comment,
    role: comment.role || comment.authorRole || '',
    date: comment.date || comment.timestamp || '',
    replies: normalizeArray(comment.replies)
      .map(normalizeComment)
      .filter(Boolean),
  };
}

function normalizeFrequencyMetrics(apiItem, displayData) {
  const rawMetrics = normalizeObject(apiItem.frequencyMetrics) || normalizeObject(displayData?.frequencyMetrics);
  const stationCoverage = normalizeArray(apiItem.stationCoverage);
  const measurementPattern =
    normalizeObject(apiItem.measurementPattern) ||
    normalizeObject(rawMetrics?.measurementPattern);

  if (!rawMetrics && stationCoverage.length === 0 && !measurementPattern) {
    return null;
  }

  return {
    caseCoverage: parseOptionalNumber(rawMetrics?.caseCoverage) ?? parseOptionalNumber(apiItem.caseCoverage),
    stationCount: parseOptionalNumber(rawMetrics?.stationCount) ?? parseOptionalNumber(apiItem.stationCount),
    totalCases: parseOptionalNumber(rawMetrics?.totalCases),
    casesWithParameter: parseOptionalNumber(rawMetrics?.casesWithParameter),
    stationCoverage: stationCoverage.length > 0 ? stationCoverage : normalizeArray(rawMetrics?.stationCoverage),
    avgMeasurementsPerCase: parseOptionalNumber(rawMetrics?.avgMeasurementsPerCase),
    medianMeasurementsPerCase: parseOptionalNumber(rawMetrics?.medianMeasurementsPerCase),
    totalMeasurements: parseOptionalNumber(rawMetrics?.totalMeasurements),
    continuousCases: parseOptionalNumber(rawMetrics?.continuousCases),
    singleMeasurementCases: parseOptionalNumber(rawMetrics?.singleMeasurementCases),
    measurementPattern,
  };
}

function normalizeWorkingListRequest(request) {
  if (!request || typeof request !== 'object') {
    return null;
  }

  return {
    parameterId: request.parameterId,
    requestedBy: request.requestedBy || '',
    createdAt: request.createdAt || null,
  };
}

function transformWorkingList(apiList) {
  if (!apiList || typeof apiList !== 'object') {
    return null;
  }

  const usageContexts = normalizeArray(apiList.usageContexts).map(context => String(context || '').trim()).filter(Boolean);
  const inferredListType =
    apiList.listType
    || (
      usageContexts.length === 1
        ? (usageContexts[0] === 'extraction' ? 'extraction' : 'criteria')
        : 'generic'
    );

  return {
    id: apiList.id,
    name: apiList.name || '',
    listType: inferredListType,
    usageContexts: usageContexts.length > 0 ? usageContexts : ['cohort', 'extraction'],
    isPrivate: Boolean(apiList.isPrivate),
    isDefault: Boolean(apiList.isDefault),
    createdBy: apiList.createdBy || '',
    parameterIds: normalizeArray(apiList.parameterIds).map(id => Number(id)),
    additionRequests: normalizeArray(apiList.additionRequests)
      .map(normalizeWorkingListRequest)
      .filter(Boolean),
    createdAt: apiList.createdAt || null,
    updatedAt: apiList.updatedAt || null,
  };
}

function transformDataRequest(apiRequest) {
  if (!apiRequest || typeof apiRequest !== 'object') {
    return null;
  }

  const payload = normalizeObject(apiRequest.requestPayload) || {};
  const selectedParameters = normalizeArray(payload.selectedParameters)
    .map(parameter => {
      if (!parameter || typeof parameter !== 'object') {
        return null;
      }

      return {
        id: parameter.id ?? parameter.parameterId ?? parameter.parameter_id,
        name: parameter.name || parameter.localName || parameter.parameterName || '',
      };
    })
    .filter(Boolean);
  const parameterIds = normalizeArray(apiRequest.parameterIds)
    .map(id => Number(id))
    .filter(Number.isInteger);
  const variableCount =
    selectedParameters.length > 0
      ? selectedParameters.length
      : parseOptionalNumber(payload.selectedParameterCount) ?? parameterIds.length;

  return {
    id: apiRequest.id,
    title: apiRequest.title || '',
    requestType: apiRequest.requestType || 'feasibility',
    status: apiRequest.status || 'pending_approval',
    workingListId: apiRequest.workingListId ?? null,
    workingListName: apiRequest.workingListName || '',
    workingListUsageContexts: normalizeArray(apiRequest.workingListUsageContexts),
    parameterIds,
    cohortName: apiRequest.cohortName || payload.cohortName || '',
    cohortId: apiRequest.cohortReference || payload.cohortId || '',
    requestedBy: apiRequest.requestedBy || '',
    approvedBy: apiRequest.approvedBy || '',
    requestedAt: apiRequest.createdAt || null,
    approvedAt: apiRequest.approvedAt || null,
    completedAt: apiRequest.completedAt || null,
    rejectionReason: apiRequest.rejectionReason || '',
    notes: apiRequest.notes || '',
    estimatedPatients: parseOptionalNumber(payload.estimatedPatients),
    variableCount,
    variables: selectedParameters,
    requestPayload: payload,
  };
}

function normalizeWorkingListSelection(selection) {
  const activeListIds = normalizeArray(selection?.activeListIds)
    .map(id => Number(id))
    .filter(Number.isInteger)
    .slice(0, 2);

  return {
    activeListIds,
    showOnlyActiveListParams: Boolean(selection?.showOnlyActiveListParams && activeListIds.length > 0),
    listComparisonFilter:
      activeListIds.length === 2
        ? (selection?.listComparisonFilter || 'all')
        : 'all',
  };
}

function buildCatalogQueryString(filters = {}) {
  const params = new URLSearchParams();

  const appendValue = (key, value) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      const normalizedValues = value
        .map(item => String(item).trim())
        .filter(Boolean);
      if (normalizedValues.length > 0) {
        params.set(key, normalizedValues.join(','));
      }
      return;
    }

    const normalizedValue = String(value).trim();
    if (normalizedValue) {
      params.set(key, normalizedValue);
    }
  };

  appendValue('q', filters.q);
  appendValue('domain', filters.domain);
  appendValue('sensitivity', filters.sensitivity);
  appendValue('source_system', filters.sourceSystem);
  appendValue('omop_status', filters.omopStatus);
  appendValue('tags', filters.tags);
  appendValue('stations', filters.stations);
  appendValue('ordering', filters.ordering);

  return params.toString();
}

function buildCatalogCollectionUrl(endpoint, filters = {}) {
  const queryString = buildCatalogQueryString(filters);
  return queryString ? `${buildCatalogUrl(endpoint)}?${queryString}` : buildCatalogUrl(endpoint);
}

function transformSavedSet(apiSet) {
  if (!apiSet || typeof apiSet !== 'object') {
    return null;
  }

  return {
    id: apiSet.id,
    name: apiSet.name || '',
    type: apiSet.setType || 'parameters',
    description: apiSet.description || '',
    isPublic: Boolean(apiSet.isPublic),
    createdBy: apiSet.createdBy || '',
    items: apiSet.items && typeof apiSet.items === 'object' ? apiSet.items : {},
    icon: apiSet.icon || '📦',
    color: apiSet.color || 'primary',
    createdAt: apiSet.createdAt || null,
    updatedAt: apiSet.updatedAt || null,
  };
}
/**
 * Transform a single API parameter item to the shape the frontend expects.
 */
function transformParameter(apiItem) {
  const displayData =
    normalizeObject(apiItem.displayData);
  const synonyms = normalizeArray(apiItem.synonyms)
    .map(normalizeSynonym)
    .filter(Boolean);
  const approvedSynonyms = synonyms.filter(synonym => synonym.approved !== false);
  const frequencyMetrics = normalizeFrequencyMetrics(apiItem, displayData);
  const caseCoverage = parseOptionalNumber(apiItem.caseCoverage);
  const stationCount = parseOptionalNumber(apiItem.stationCount);
  const omopMapping = normalizeObject(apiItem.omopMapping) || normalizeObject(displayData?.omopMapping);
  const hasStructuredDetailData = Boolean(
    frequencyMetrics ||
    normalizeArray(apiItem.stationCoverage).length > 0 ||
    normalizeObject(apiItem.measurementPattern) ||
    omopMapping
  );

  return {
    id: apiItem.id,
    parameterId: apiItem.parameterId,
    name: apiItem.localName,
    omopName: apiItem.omopName || '',
    description: apiItem.description || '',
    tags: normalizeArray(apiItem.baseTags),
    stations: normalizeArray(apiItem.stations),
    frequency: caseCoverage,
    omopStatus: apiItem.omopStatus || omopMapping?.status || 'pending',
    omopCode: apiItem.omopCode || omopMapping?.topMatch?.conceptCode || '',
    omopDomain: apiItem.domain || omopMapping?.topMatch?.domain || '',
    omopDescription: displayData?.omopDescription || '',
    omopVocabulary: omopMapping?.topMatch?.vocabulary || '',
    sensitivity: apiItem.sensitivity || '',
    sourceSystem: apiItem.sourceSystem || '',
    stationCount,
    synonyms,
    approvedSynonyms,
    availableSince: apiItem.availableSince || apiItem.statistics?.availableSince || null,
    usagePattern: apiItem.usagePattern || apiItem.statistics?.usagePattern || '',
    frequencyMetrics,
    units: normalizeArray(displayData?.units),
    dataMapping:
      displayData?.dataMapping && typeof displayData.dataMapping === 'object'
        ? displayData.dataMapping
        : null,
    stationDetails: normalizeArray(displayData?.stationDetails),
    tagDetails: normalizeArray(displayData?.tagDetails),
    similarParameters: normalizeArray(displayData?.similarParameters),
    omopMapping,
    sourceSystemInfo: {
      name: apiItem.sourceSystem || displayData?.dataMapping?.system || '',
      updateFrequency: displayData?.sourceSystem?.updateFrequency || null,
      lastSync: displayData?.sourceSystem?.lastSync || null,
      reliability: displayData?.sourceSystem?.reliability || null,
    },
    engineerNotes: normalizeArray(displayData?.engineerNotes),
    comments: normalizeArray(displayData?.comments || omopMapping?.comments)
      .map(normalizeComment)
      .filter(Boolean),
    displayData: displayData || {},
    hasDetailData: Boolean(displayData) || hasStructuredDetailData,
    isFavorite: Boolean(displayData?.isFavorite),
  };
}

// ─── public API ──────────────────────────────────────────────────────────────

/**
 * Fetch all catalog parameters (paginated – fetches all pages).
 * Returns { parameters, source } where source is 'api' or 'error'.
 */
export async function getParameters(filters = {}) {
  try {
    let allResults = [];
    let url = buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.LIST, {
      ...filters,
      pageSize: undefined,
    });
    url = `${url}${url.includes('?') ? '&' : '?'}page_size=200`;
    let pageCount = 0;

    while (url) {
      pageCount += 1;
      if (pageCount > MAX_PAGES) {
        throw new Error(`Catalog pagination exceeded ${MAX_PAGES} pages`);
      }

      const data = await apiFetch(url, { allowAnonymousRetry: true });
      allResults = allResults.concat(data.results || []);
      url = data.next || null;
    }

    return {
      parameters: allResults.map(transformParameter),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getParameters failed:', err.message);
    return { parameters: [], source: 'error', error: err };
  }
}

/**
 * Fetch a single parameter by database id or stable parameter identifier.
 */
export async function getParameter(id) {
  try {
    const data = await apiFetch(
      buildCatalogUrl(API_ENDPOINTS.CATALOG.DETAIL.replace('{id}', getParameterLookupId(id))),
      { allowAnonymousRetry: true }
    );
    return { parameter: transformParameter(data), source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getParameter failed:', err.message);
    return { parameter: null, source: 'error', error: err };
  }
}

/**
 * Free-text + filter search.
 * Returns { parameters, source }.
 */
export async function searchParameters({ q = '', domain, sensitivity, sourceSystem, omopStatus, tags, stations } = {}) {
  try {
    const data = await apiFetch(
      buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.SEARCH, {
        q,
        domain,
        sensitivity,
        sourceSystem,
        omopStatus,
        tags,
        stations,
      }),
      { allowAnonymousRetry: true }
    );
    return {
      parameters: (data.results || []).map(transformParameter),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] searchParameters failed:', err.message);
    return { parameters: [], source: 'error', error: err };
  }
}

/**
 * Aggregate stats: totalParameters, mappedCount, validatedCount, pendingCount,
 * domainDistribution, sensitivityDistribution.
 */
export async function getCatalogStats() {
  try {
    const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.CATALOG.STATS), { allowAnonymousRetry: true });
    return { stats: data, source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogStats failed:', err.message);
    return { stats: null, source: 'error', error: err };
  }
}

/**
 * Distinct tags with usage counts.
 * Returns { tags: [{ value, count }], source }.
 */
export async function getCatalogTags(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.TAGS, filters), {
      allowAnonymousRetry: true,
    });
    return { tags: data.results || [], source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogTags failed:', err.message);
    return { tags: [], source: 'error', error: err };
  }
}

/**
 * Distinct stations with usage counts.
 * Returns { stations: [{ value, count }], source }.
 */
export async function getCatalogStations(filters = {}) {
  try {
    const data = await apiFetch(buildCatalogCollectionUrl(API_ENDPOINTS.CATALOG.STATIONS, filters), {
      allowAnonymousRetry: true,
    });
    return { stations: data.results || [], source: 'api' };
  } catch (err) {
    console.warn('[catalogService] getCatalogStations failed:', err.message);
    return { stations: [], source: 'error', error: err };
  }
}

/**
 * Simple connectivity check — resolves true if the API is reachable.
 */
export async function isCatalogApiReachable() {
  try {
    await apiFetch(buildCatalogUrl(API_ENDPOINTS.CATALOG.STATS), { allowAnonymousRetry: true });
    return true;
  } catch {
    return false;
  }
}

export async function getWorkingLists() {
  try {
    const data = await apiFetch(buildListUrl());
    return {
      lists: normalizeArray(data).map(transformWorkingList).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getWorkingLists failed:', err.message);
    return { lists: [], source: 'error', error: err };
  }
}

export async function getSavedSets() {
  try {
    const data = await apiFetch(buildSetUrl());
    return {
      sets: normalizeArray(data).map(transformSavedSet).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getSavedSets failed:', err.message);
    return { sets: [], source: 'error', error: err };
  }
}

export async function getWorkingListSelection() {
  try {
    const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.LISTS.SELECTION));
    return {
      selection: normalizeWorkingListSelection(data),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getWorkingListSelection failed:', err.message);
    return {
      selection: normalizeWorkingListSelection(null),
      source: 'error',
      error: err,
    };
  }
}

export async function updateWorkingListSelection(selection) {
  const data = await apiFetch(buildCatalogUrl(API_ENDPOINTS.LISTS.SELECTION), {
    method: 'PATCH',
    body: selection,
  });
  return normalizeWorkingListSelection(data);
}
export async function createWorkingList({
  name,
  listType = 'generic',
  usageContexts,
  isPrivate = true,
}) {
  const data = await apiFetch(buildListUrl(), {
    method: 'POST',
    body: {
      name,
      listType,
      usageContexts: usageContexts || (
        listType === 'criteria'
          ? ['cohort']
          : listType === 'extraction'
            ? ['extraction']
            : ['cohort', 'extraction']
      ),
      isPrivate,
    },
  });
  return transformWorkingList(data);
}

export async function updateWorkingList(id, updates) {
  const nextUpdates = { ...updates };
  if (nextUpdates.listType && !nextUpdates.usageContexts) {
    nextUpdates.usageContexts =
      nextUpdates.listType === 'criteria'
        ? ['cohort']
        : nextUpdates.listType === 'extraction'
          ? ['extraction']
          : ['cohort', 'extraction'];
  }
  const data = await apiFetch(buildListUrl(`${id}`), {
    method: 'PATCH',
    body: nextUpdates,
  });
  return transformWorkingList(data);
}

export async function deleteWorkingList(id) {
  await apiFetch(buildListUrl(`${id}`), {
    method: 'DELETE',
  });
}

export async function duplicateWorkingList(id) {
  const data = await apiFetch(buildListUrl(`${id}/duplicate`), {
    method: 'POST',
  });
  return transformWorkingList(data);
}

export async function getDataRequests() {
  try {
    const data = await apiFetch(buildRequestUrl());
    return {
      requests: normalizeArray(data).map(transformDataRequest).filter(Boolean),
      source: 'api',
    };
  } catch (err) {
    console.warn('[catalogService] getDataRequests failed:', err.message);
    return { requests: [], source: 'error', error: err };
  }
}

export async function createDataRequest({
  title,
  requestType,
  workingListId,
  cohortName,
  cohortReference,
  notes,
  requestPayload,
}) {
  const data = await apiFetch(buildRequestUrl(), {
    method: 'POST',
    body: {
      title,
      requestType,
      workingList: workingListId,
      cohortName,
      cohortReference,
      notes,
      requestPayload,
    },
  });
  return transformDataRequest(data);
}

export async function approveDataRequest(id) {
  const data = await apiFetch(buildRequestUrl(`${id}/approve`), {
    method: 'POST',
    body: {},
  });
  return transformDataRequest(data);
}

export async function completeDataRequest(id) {
  const data = await apiFetch(buildRequestUrl(`${id}/complete`), {
    method: 'POST',
    body: {},
  });
  return transformDataRequest(data);
}

export async function rejectDataRequest(id, reason = '') {
  const data = await apiFetch(buildRequestUrl(`${id}/reject`), {
    method: 'POST',
    body: { reason },
  });
  return transformDataRequest(data);
}

export async function addParameterToWorkingList(listId, parameterId) {
  const data = await apiFetch(buildListUrl(`${listId}/parameters`), {
    method: 'POST',
    body: { parameterId },
  });

  return {
    action: data?.action || 'unknown',
    message: data?.message || '',
    list: transformWorkingList(data?.list),
  };
}

export async function removeParameterFromWorkingList(listId, parameterId) {
  const data = await apiFetch(buildListUrl(`${listId}/parameters/${parameterId}`), {
    method: 'DELETE',
  });

  return {
    action: data?.action || 'unknown',
    message: data?.message || '',
    list: transformWorkingList(data?.list),
  };
}

export async function createManualMapping(parameterId, concept) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/manual'), {
    method: 'POST',
    body: {
      conceptId: concept.conceptId,
      conceptCode: concept.conceptCode || '',
      conceptName: concept.conceptName,
      vocabulary: concept.vocabulary || '',
      domain: concept.domain || '',
      confidenceScore: concept.confidenceScore,
      justification: concept.justification || '',
      source: 'manual',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function validateCurrentMapping(parameterId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/validate'), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCurrentMapping(parameterId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/reject'), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCurrentMapping(parameterId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/comment'), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCurrentMapping(parameterId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/current/vote'), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createMappingProposal(parameterId, concept) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'mapping/proposals'), {
    method: 'POST',
    body: {
      conceptId: concept.conceptId,
      conceptCode: concept.conceptCode || '',
      conceptName: concept.conceptName,
      vocabulary: concept.vocabulary || '',
      domain: concept.domain || '',
      confidenceScore: concept.confidenceScore,
      justification: concept.justification || '',
      metadata: concept.metadata || {},
      source: concept.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveMappingProposal(parameterId, proposalId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectMappingProposal(parameterId, proposalId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteMappingProposal(parameterId, proposalId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentMappingProposal(parameterId, proposalId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `mapping/proposals/${proposalId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createCatalogSynonym(parameterId, synonym) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'synonyms'), {
    method: 'POST',
    body: {
      term: synonym.term,
      language: synonym.language || 'und',
      context: synonym.context || '',
      source: synonym.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveCatalogSynonym(parameterId, synonymId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCatalogSynonym(parameterId, synonymId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCatalogSynonym(parameterId, synonymId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCatalogSynonym(parameterId, synonymId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function deleteCatalogSynonym(parameterId, synonymId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `synonyms/${synonymId}`), {
    method: 'DELETE',
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function createCatalogTag(parameterId, tag) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, 'tags'), {
    method: 'POST',
    body: {
      name: tag.name,
      icon: tag.icon || '',
      isPrivate: Boolean(tag.isPrivate),
      source: tag.source || 'user',
    },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function approveCatalogTag(parameterId, tagId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/approve`), {
    method: 'POST',
    body: {},
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function rejectCatalogTag(parameterId, tagId, reason = '') {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/reject`), {
    method: 'POST',
    body: { reason },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function voteCatalogTag(parameterId, tagId, vote) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/vote`), {
    method: 'POST',
    body: { vote },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function commentCatalogTag(parameterId, tagId, text) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}/comment`), {
    method: 'POST',
    body: { text },
  });

  return { parameter: transformParameter(data), source: 'api' };
}

export async function deleteCatalogTag(parameterId, tagId) {
  const data = await apiFetch(buildCatalogDetailUrl(parameterId, `tags/${tagId}`), {
    method: 'DELETE',
  });

  return { parameter: transformParameter(data), source: 'api' };
}
